import type { AppointmentPlanningExistingSourceRow } from "../../../../../../../../../../../_shared/_logic/appointments-contract"
import { formatDateOnly } from "../../../../../../../../../../../_shared/_logic/dates"
import type { InboxCaseBatchWorkflow } from "../../../../../../../../../../../../../api/kehrblatt/inbox-cases"
import type {
  PlanAppointmentSettings,
  PlanAppointmentSourcePreview,
  PlanPropertySummary,
  PlanScheduleEventSummary,
  PlanWorkOrderSummary,
} from "../contract"

export type PlanAppointmentSourceRowError = {
  action_case_id: string | null
  code: string | null
  message: string
}

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === "object" && !Array.isArray(value))

export const objectValue = (value: unknown): Record<string, unknown> =>
  isRecord(value) ? value : {}

export const stringValue = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim()

    if (typeof value === "number" && Number.isFinite(value)) return String(value)
  }

  return ""
}

export const numberValue = (...values: unknown[]): number | null => {
  for (const value of values) {
    if (typeof value === "number" && Number.isFinite(value)) return value

    if (typeof value === "string" && value.trim()) {
      const parsed = Number(value)

      if (Number.isFinite(parsed)) return parsed
    }
  }

  return null
}

const propertyView = (property: PlanPropertySummary): Property =>
  ({
    id: property.id,
    street: property.street,
    house_number: property.houseNumber || null,
    zip: property.postalCode || null,
    city: property.city || null,
    country: property.country || null,
    address_extra: property.addressExtra || null,
    address_label: property.addressLabel,
    label: property.addressLabel,
  }) as unknown as Property

const displayOrderTypes = (
  workOrder: Pick<PlanWorkOrderSummary, "orderTypeIds" | "orderTypeLabel">,
): WorkOrderOrderTypeItem[] => {
  const label = workOrder.orderTypeLabel.trim()
  const ids = workOrder.orderTypeIds

  if (!ids.length) {
    return label
      ? [{ order_type_id: null, internal_label: label, public_label: label, description: null }]
      : []
  }

  if (ids.length === 1) {
    const itemLabel = label || ids[0]!

    return [
      {
        order_type_id: ids[0]!,
        internal_label: itemLabel,
        public_label: itemLabel,
        description: null,
      },
    ]
  }

  const rawParts = label
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
  const labels =
    rawParts.length >= ids.length
      ? [...rawParts.slice(0, ids.length - 1), rawParts.slice(ids.length - 1).join(", ")]
      : ids.map((id, index) => rawParts[index] ?? id)

  return ids.map((id, index) => ({
    order_type_id: id,
    internal_label: labels[index]!,
    public_label: labels[index]!,
    description: null,
  }))
}

const workOrderView = (workOrder: PlanWorkOrderSummary): WorkOrder =>
  ({
    id: workOrder.id,
    property_id: workOrder.propertyId,
    property_unit_id: workOrder.propertyUnitId,
    property_unit_label: workOrder.propertyUnitLabel || null,
    order_number: workOrder.orderNumber || null,
    order_types: displayOrderTypes(workOrder),
    due_date: workOrder.dueDate,
    estimated_duration_min: workOrder.estimatedDurationMin,
    assigned_employee_id: workOrder.assignedEmployeeId,
    allowed_employee_ids: workOrder.allowedEmployeeIds,
    current_schedule_event_id: workOrder.currentScheduleEventId,
    status: workOrder.status,
    recipients: [],
  }) as unknown as WorkOrder

const eventView = (event: PlanScheduleEventSummary | null): ScheduleEvent | null => {
  if (!event) return null

  return {
    id: event.id,
    work_order_id: event.workOrderId,
    property_id: event.propertyId,
    employee_id: event.employeeId,
    employee_name: event.employeeName || null,
    start_at: event.startAt,
    end_at: event.endAt,
    timeframe_start_at: event.timeframeStartAt ?? event.startAt,
    timeframe_end_at: event.timeframeEndAt ?? event.endAt,
    customer_window_start_at: event.customerWindowStartAt,
    customer_window_end_at: event.customerWindowEndAt,
    status: event.status,
    flex_type: event.flexType ?? "HARD",
  } as ScheduleEvent
}

const eventFor = (preview: PlanAppointmentSourcePreview, id: string | null) =>
  id ? eventView(preview.scheduleEventsById[id] ?? null) : null

export const buildSourceRows = (
  workflow: InboxCaseBatchWorkflow,
  preview: PlanAppointmentSourcePreview,
): AppointmentPlanningExistingSourceRow[] =>
  workflow.items.flatMap((item): AppointmentPlanningExistingSourceRow[] => {
    const context = preview.rowContextByActionCaseId[item.action_case.id]

    if (!context) return []

    const workOrder = preview.workOrdersById[context.workOrderId]
    const property = preview.propertiesById[context.propertyId]

    if (!workOrder || !property) return []

    const activeEvent = eventFor(preview, context.activeScheduleEventId)
    const sourceEvent = eventFor(preview, context.sourceScheduleEventId)
    const canceledEvent = eventFor(preview, context.canceledScheduleEventId)

    return [
      {
        id: item.action_case.id,
        workOrder: workOrderView(workOrder),
        property: propertyView(property),
        previousEvent: activeEvent,
        sourceScheduleEvent: sourceEvent ?? canceledEvent,
        sourceScheduleEventId: context.sourceScheduleEventId,
        canceledScheduleEventId: context.canceledScheduleEventId,
        planningTargetScheduleEventId: context.activeScheduleEventId,
        requesterCustomerId: context.requesterCustomerId,
        defaultPlanningStrategy: context.defaultPlanningStrategy,
      } satisfies AppointmentPlanningExistingSourceRow,
    ]
  })

const relevantPlanItemIds = (workflow: InboxCaseBatchWorkflow) =>
  workflow.items
    .filter(
      (item) =>
        item.compatible &&
        (item.steps.some((step) => step.step_key === "plan_appointment") ||
          item.action_case.requirements.includes("PLAN_APPOINTMENT")),
    )
    .map((item) => item.action_case.id)

export const planSourceRowErrors = (
  workflow: InboxCaseBatchWorkflow,
  preview: PlanAppointmentSourcePreview | null,
): PlanAppointmentSourceRowError[] => {
  if (!preview)
    return [
      { action_case_id: null, code: "preview_missing", message: "Terminplanungsdaten fehlen." },
    ]

  const errors = Object.entries(preview.rowErrorsByActionCaseId).map(([actionCaseId, message]) => ({
    action_case_id: actionCaseId,
    code: "source_context_error",
    message,
  }))
  const covered = new Set([
    ...Object.keys(preview.rowContextByActionCaseId),
    ...Object.keys(preview.rowErrorsByActionCaseId),
  ])
  for (const actionCaseId of relevantPlanItemIds(workflow)) {
    if (!covered.has(actionCaseId)) {
      errors.push({
        action_case_id: actionCaseId,
        code: "source_context_missing",
        message: "Terminplanungskontext fehlt für diese Inbox-Zeile.",
      })
    }
  }

  return errors
}

export const sourceStartDate = (sourceRows: AppointmentPlanningExistingSourceRow[]) => {
  const candidate = sourceRows
    .map((row) => row.sourceScheduleEvent?.start_at ?? row.previousEvent?.start_at)
    .find((value): value is string => Boolean(value))

  return formatDateOnly(candidate || new Date())
}

export const planningSettings = (
  preview: PlanAppointmentSourcePreview | null,
): PlanAppointmentSettings =>
  preview?.planningSettings ?? {
    flex_slot_minutes: 0,
    flex_slots_per_day: 0,
  }
